"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/ui/loader";
import { useEmbeddedSessionExpiry } from "@/hooks/useEmbeddedSessionExpiry";
import {
  type SpondProfileSummary,
  useSpondProfile,
} from "@/hooks/useSpondProfile";
import { todayAsIsoDate } from "@/utils/date";
import { ADULT_AGE, checkAttendeeAge } from "@/utils/eventAgeGroup";
import { signupErrorKey } from "@/utils/signupErrorKey";
import { useTranslations } from "next-intl";
import { type FormEvent, useId, useState } from "react";
import ContinueInAppState from "./ContinueInAppState";

interface ReadOnlyRow {
  id: string;
  label: string;
  value: string;
}

/** Rejections that mean Core still has no usable date of birth for the user. */
const DOB_ERROR_KEYS: ReadonlySet<string> = new Set([
  "dateOfBirthRequired",
  "age_unknown",
  "age_invalid",
]);

/**
 * A date of birth is asked for whenever the account has none, not only for 18+
 * events: Spond collects it at account signup, and it is worth holding
 * regardless. Note the explicit `=== false` — an unloaded profile must not read
 * as "no date". On age-gated events, an ambiguous truncated record (`isAdult`
 * omitted) also needs a full date so we do not silently skip the 18+ check.
 */
function readProfileAge(
  profile: SpondProfileSummary | undefined,
  requireAdultAge: boolean,
) {
  const recorded = requireAdultAge && profile?.dateOfBirthKnown === true;
  return {
    dateOfBirthMissing:
      profile?.dateOfBirthKnown === false ||
      (recorded && profile.isAdult === undefined),
    blockedByProfileAge: recorded && profile.isAdult === false,
  };
}

interface AdultDetailsStepProps {
  eventId: string;
  /**
   * Profile to confirm, shown read-only. When omitted, the step fetches it from
   * `/api/profile` (the logged-in user's name/phone). Pass it to override the
   * fetch (tests, or threading create-account data).
   */
  profile?: SpondProfileSummary;
  requireAdultAge?: boolean;
  /**
   * Called once the signup is confirmed (advances to the success step). Receives
   * the confirmed signup's `attendanceUrl` (the attendee's custom link) so the
   * funnel can send them there when they close the sheet.
   */
  onSubmitted: (attendanceUrl?: string) => void;
}

/**
 * Adult self-signup details (design `14510:5388`, "Confirm your details").
 */
export default function AdultDetailsStep({
  eventId,
  profile,
  requireAdultAge = false,
  onSubmitted,
}: AdultDetailsStepProps) {
  const t = useTranslations("publicEventSignup");
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";

  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [dobError, setDobError] = useState<string | null>(null);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  // Revealed when the server says we still need a DOB (e.g. profile fetch failed
  // client-side so needsDateOfBirth looked false, then signup 422'd).
  const [forceAskDob, setForceAskDob] = useState(false);
  const consentId = useId();
  const {
    profile: effectiveProfile,
    loading: loadingProfile,
    status: profileStatus,
  } = useSpondProfile(profile);
  const sessionExpiry = useEmbeddedSessionExpiry(profileStatus);

  const { dateOfBirthMissing, blockedByProfileAge } = readProfileAge(
    effectiveProfile,
    requireAdultAge,
  );
  const needsDateOfBirth = forceAskDob || dateOfBirthMissing;

  function dateOfBirthError(): string | null {
    if (!needsDateOfBirth) return null;
    if (!dateOfBirth) return t("dobRequired");

    const verdict = checkAttendeeAge({ attendeeType: "SELF", dateOfBirth });
    if (verdict === "invalid") return t("dobInvalid");
    // Being 18+ is the event's rule, not a condition of giving the date: on an
    // event with no age policy we collect it and let a younger adult through.
    if (requireAdultAge && verdict !== "ok") {
      return t("adultDetails.dobTooYoung", { age: ADULT_AGE });
    }
    return null;
  }

  function validate(): boolean {
    const dob = dateOfBirthError();
    const consent = consentAccepted ? null : t("adultDetails.consentRequired");
    setDobError(dob);
    setConsentError(consent);
    return !dob && !consent;
  }

  function showRejection(status: number, data: { errorKey?: unknown }) {
    const errorKey =
      typeof data?.errorKey === "string" ? data.errorKey : undefined;
    if (errorKey && DOB_ERROR_KEYS.has(errorKey)) setForceAskDob(true);
    setFormError(t(signupErrorKey(status, errorKey)));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${basePath}/api/events/${eventId}/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attendeeType: "SELF",
          signupAcknowledgementAccepted: true,
          ...(needsDateOfBirth ? { dateOfBirth } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        onSubmitted(
          typeof data?.attendanceUrl === "string"
            ? data.attendanceUrl
            : undefined,
        );
        return;
      }
      // An expired session inside the WebView is only recoverable via the app.
      if (sessionExpiry.reportSubmitStatus(res.status)) return;
      showRejection(res.status, data);
    } catch {
      setFormError(t("signupErrorGeneric"));
    } finally {
      setSubmitting(false);
    }
  }

  const fields = [
    {
      id: "first-name",
      label: t("firstNameLabel"),
      value: effectiveProfile?.firstName,
    },
    {
      id: "last-name",
      label: t("lastNameLabel"),
      value: effectiveProfile?.lastName,
    },
    {
      id: "phone",
      label: t("phoneLabel"),
      value: effectiveProfile?.phoneNumber,
    },
  ].filter((f): f is ReadOnlyRow => !!f.value);
  const hasProfile = fields.length > 0;

  if (sessionExpiry.expired) {
    return <ContinueInAppState />;
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">{t("adultDetails.header")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("adultDetails.description")}
          {/* Only meaningful once the read-only fields are actually on screen. */}
          {hasProfile && ` ${t("adultDetails.appOnlyNote")}`}
        </p>
      </div>

      {loadingProfile && (
        <div className="flex justify-center py-6">
          <Loader />
        </div>
      )}

      {hasProfile && (
        <div className="flex flex-col gap-4">
          {fields.map(({ id, label, value }) => (
            <div key={id} className="flex flex-col gap-1.5">
              <Label htmlFor={`adult-${id}`}>{label}</Label>
              <Input id={`adult-${id}`} value={value} disabled readOnly />
            </div>
          ))}
        </div>
      )}

      {needsDateOfBirth && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="adult-dob">{t("dobLabel")}</Label>
          <Input
            id="adult-dob"
            type="date"
            className="appearance-none"
            max={todayAsIsoDate()}
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            aria-invalid={dobError ? true : undefined}
            aria-describedby={dobError ? "adult-dob-error" : undefined}
          />
          {dobError && (
            <p id="adult-dob-error" className="text-sm text-destructive">
              {dobError}
            </p>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex items-start gap-2 text-sm">
          <Checkbox
            id={consentId}
            checked={consentAccepted}
            aria-invalid={!!consentError}
            aria-describedby={consentError ? "adult-consent-error" : undefined}
            onCheckedChange={(c) => setConsentAccepted(c === true)}
          />
          <Label htmlFor={consentId} className="text-sm font-normal">
            {t("adultDetails.consent")}
          </Label>
        </div>
        {consentError && (
          <p id="adult-consent-error" className="text-sm text-destructive">
            {consentError}
          </p>
        )}
      </div>

      <div className="mt-auto flex flex-col gap-3 pt-4">
        {blockedByProfileAge && (
          <p className="text-sm text-destructive" role="alert">
            {t("adultDetails.dobTooYoung", { age: ADULT_AGE })}
          </p>
        )}
        {formError && (
          <p className="text-sm text-destructive" role="alert">
            {formError}
          </p>
        )}
        <Button
          type="submit"
          variant="default"
          disabled={submitting || blockedByProfileAge || loadingProfile}
          className="w-full rounded-full"
        >
          {t("adultDetails.submit")}
        </Button>
      </div>
    </form>
  );
}
